import { ExpressionBasedColumnPattern } from '../../dataflow/expression-based.column.pattern';
import { ExpressionBasedColumnBase } from '../../dataflow/expression-based.column.base';
import { ExpressionBasedColumn } from '../../dataflow/expression-based.column';
import { Transformation } from 'app/model/dataflow/transformation';
import { DataFlowLoggingService } from 'app/authoring/services/dataflow-logger.service';
import { Metamodel } from 'app/model/base/model';
import { DataFlowExpressionUtils } from 'app/model/adapters/dataflow/expression.util';
import { DataFlowSchemaAdapterUtils } from './dataflow-schema-adapter-utils';
import { BasePivotTransformation } from 'app/model/dataflow/base-pivottransformation';
import { BaseGroupByTransformation } from 'app/model/dataflow/base-group-by-transformation';
import { Constants } from 'app/shared/shared-constants';
import { FoldDownTransformation } from 'app/model/dataflow/folddowntransformation';
import { IResourceContext } from 'shell-models-lib';

export class ExpressionBasedColumnVisitorAdapterUtils {
    public static async writeToDsl(contextData: IResourceContext, expressionBasedColumns: ExpressionBasedColumnBase[], entity: Transformation): Promise<string> {
        await DataFlowLoggingService.logExpressionColumnInfos(contextData, expressionBasedColumns, entity.name, Metamodel.modelOf(entity).name, entity.owner.name);
        const columns = expressionBasedColumns.map((column) => {
            if (column instanceof ExpressionBasedColumnPattern) {
                const expressions: string[] = [];
                const hierarchyLevel: string = column.hierarchyLevelExpression.expression ? `${column.hierarchyLevelExpression.expression}, ` : '';
                const columnMode = column.deepHierarchy ? 'every' : 'each';

                if (column.regexString) {
                    expressions.push(`${columnMode}(${hierarchyLevel}patternMatch(\`${column.regexString}\` ${column.templateCondition.expression ? `, ${column.templateCondition.expression}` : ''})`);
                } else {
                    expressions.push(`${columnMode}(${hierarchyLevel}match(${column.templateCondition.expression})`);
                }

                if (!(column.owner instanceof FoldDownTransformation)) {
                    for (const nestedColumn of column.columns) {
                        expressions.push(`, ${DataFlowExpressionUtils.wrapExpressionWithEmptyValue(nestedColumn.columnNameExpression.expression)} ` +
                            `= ${DataFlowExpressionUtils.wrapExpressionWithEmptyValue(nestedColumn.dataFlowExpression.expression)}`);

                        if (column.fixedReturnTypeExpression.expression) {
                            expressions.push(` ? ${column.fixedReturnTypeExpression.expression}`);
                        }
                    }
                }
                expressions.push(')');

                return expressions.join('');
            } else if (column instanceof ExpressionBasedColumn) {
                const equalsOperator = column.column.isTransient ? ':=' : '=';

                return `${column.column.serializableColumnName} ${equalsOperator} ${DataFlowExpressionUtils.wrapExpressionWithEmptyValue(column.dataFlowExpression.expression)}`
                    + (column.fixedReturnTypeExpression.expression ? ` ? ${column.fixedReturnTypeExpression.expression}` : '');
            }

            return '';
        });

        return columns.join(',\n\t\t');
    }

    public static writeColumnExpressionToDsl(expressionBasedColumns: string): string {
        return `${DataFlowExpressionUtils.wrapExpressionWithEmptyValue(expressionBasedColumns)}`;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public static addGroupByColumns(transformation: BaseGroupByTransformation, expressions: any): void {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        expressions.children.forEach((expression: any) => {
            if (expression.columnOperand && expression.columnOperand()) {
                const expressionText = expression.columnOperand().getText();
                ExpressionBasedColumnVisitorAdapterUtils._addGroupByColumn(transformation, expressionText);
            } else if (expression.ASSIGN && expression.ASSIGN()) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                ExpressionBasedColumnVisitorAdapterUtils._addGroupByColumn(transformation,
                    DataFlowSchemaAdapterUtils.getColumnName(expression.columnIdentifier()),
                    expression.expression().getText());
            }
        });
    }

    public static getGroupByColumnsDsl(transformation: BaseGroupByTransformation, dslWord: string = 'groupBy'): string {
        const groupByClauses = transformation.groupByColumns.map((groupByColumn) => {
            const expressionToSave =  DataFlowExpressionUtils.wrapExpressionWithEmptyValue(groupByColumn.dataFlowExpression.expression);
            const foundColumn = transformation.distinctInputColumnRefs.find((inputColumn) => inputColumn.serializableName === expressionToSave);
            return groupByColumn.column.serializableColumnName === expressionToSave
                // we still want to set assignment in case they want to override the name i.e. Name2 = source1@Name
                || (foundColumn && foundColumn.column.serializableColumnName === groupByColumn.column.serializableColumnName)
                ? expressionToSave
                : `${groupByColumn.column.serializableColumnName} = ${expressionToSave}`;
        });

        return `${dslWord}(${groupByClauses.join(',\n\t\t')})`;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public static addPivotValues(transformation: BasePivotTransformation, context: any): void {
        const valueExpressions = context.expression();
        if (valueExpressions) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            valueExpressions.forEach((value: any) => {
                const val = value.getText();
                if (val !== 'null') {
                    transformation.pivotKeyValues.push(DataFlowExpressionUtils.extractStringConfigValue(val));
                } else {
                    transformation.nullValue = true;
                }
            });
        }
    }

    private static _addGroupByColumn(transformation: BaseGroupByTransformation, name: string, expression?: string): void {
        const groupByColumn = transformation.addGroupByColumn();
        const unescapedColumnName = Constants.dataFlowEscapedNameValidation.test(name) ? DataFlowSchemaAdapterUtils.removeWrappingCharacters(name) : name;
        groupByColumn.column.name = expression ? unescapedColumnName : DataFlowSchemaAdapterUtils.extractColumnName(unescapedColumnName);
        groupByColumn.dataFlowExpression.expression = expression || name;
    }
}
